import { useEffect, useRef, useState } from 'react';
import { clearFormDraft, readFormDraft, saveFormDraft } from '@/vacancy_request/helpers/form-draft-storage';

/**
 * Detecta que la plantilla cambió **mientras la persona estaba completando el formulario**, y
 * administra el borrador temporal que permite refrescar sin perder lo cargado (D6, D10, D12, D54).
 *
 * Se compara siempre contra **la versión con la que se dibujó el formulario**, capturada al montar,
 * y nunca contra la `templateVersion` sellada en la solicitud (D54). El formulario de edición ya se
 * dibuja con la plantilla vigente —el snapshot es para la vista de lectura (D45, D46)—, así que al
 * abrirlo no hay nada que avisar: la persona ya está viendo lo nuevo. Avisar ahí ofrecía refrescar
 * para conseguir algo que ya tenía.
 *
 * Queda un solo momento de detección, que es el que tiene algo en juego: la plantilla cambia con el
 * formulario abierto y ya hay datos cargados. Se chequea al volver a la pestaña y antes de enviar
 * (D9, D42), en los dos casos volviendo a pedir la versión, porque la plantilla que se dibuja está
 * congelada a propósito (D43).
 */
/**
 * Normaliza una versión de plantilla a número o `null`.
 *
 * El chequeo explícito de null/undefined no es redundante con `Number.isFinite`: `Number(null)` es
 * 0, y las solicitudes anteriores a este feature no tienen `templateVersion` — la proyección las
 * devuelve como `null`. Sin esto, la versión renderizada quedaría en 0, cualquier plantilla real
 * diferiría, y **toda solicitud vieja abriría el diálogo de cambio apenas se edita**.
 */
export const toTemplateVersion = (value) => {
  if (value === undefined || value === null || value === '') return null;

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * La versión con la que se dibujó el formulario. Se captura **una sola vez**, la primera que llega
 * de la plantilla, y no se vuelve a mover: es el punto fijo contra el que se compara todo.
 *
 * Devuelve `null` mientras la plantilla no haya llegado, y con `null` no se compara nada.
 *
 * No recibe la `templateVersion` sellada en la solicitud a propósito (D54). El formulario de edición
 * ya se dibuja con la plantilla vigente, así que al abrirlo la persona está viendo lo nuevo y no hay
 * nada que avisar; y comparar contra la sellada no tenía salida, porque esos dos números no se
 * acercan hasta que la solicitud se reenvía.
 */
export const resolveRenderedVersion = (previousRendered, currentTemplateVersion) => {
  if (previousRendered !== null && previousRendered !== undefined) return previousRendered;

  return toTemplateVersion(currentTemplateVersion);
};

/**
 * Si la plantilla cambió respecto de la que se dibujó.
 *
 * Cuando falta alguno de los dos datos devuelve `false`: mejor seguir que frenar a la persona por un
 * error de red o por una carga que todavía no terminó.
 */
export const hasVersionDiverged = (renderedVersion, latestVersion) => {
  if (renderedVersion === null || renderedVersion === undefined) return false;
  if (latestVersion === null || latestVersion === undefined) return false;

  return latestVersion !== renderedVersion;
};

export const useTemplateChangeGuard = ({
  draftKey,
  currentTemplateVersion,
  fetchCurrentTemplateVersion,
  isEnabled
}) => {
  const [hasTemplateChanged, setHasTemplateChanged] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Una vez que la solicitud se guardó bien, el formulario dejó de estar en juego: lo que muestre la
  // plantilla ya no cambia nada de lo enviado. Seguir chequeando sólo sirve para pedirle a la
  // persona que refresque un formulario que ya mandó, encima por encima del modal de éxito.
  const [hasBeenSubmitted, setHasBeenSubmitted] = useState(false);
  const renderedVersionRef = useRef(null);

  // El fetcher se guarda en un ref para que el efecto de abajo no dependa de su identidad: los
  // llamadores lo definen inline, y sin esto el listener de foco se desengancharía y volvería a
  // engancharse en cada render del formulario.
  const fetchVersionRef = useRef(fetchCurrentTemplateVersion);
  fetchVersionRef.current = fetchCurrentTemplateVersion;

  const currentVersion = toTemplateVersion(currentTemplateVersion);

  // La versión con la que se dibujó el formulario, capturada una sola vez: la primera que llega de
  // la plantilla. Es el único punto de comparación, tanto en creación como en edición (D54).
  //
  // No se mira la `templateVersion` sellada en la solicitud a propósito. El formulario de edición ya
  // se dibuja con la plantilla vigente, así que al abrirlo la persona está viendo lo nuevo y no hay
  // nada que avisar; y comparar contra la sellada además no tenía salida, porque esos dos números no
  // se acercan hasta que la solicitud se reenvía.
  renderedVersionRef.current = resolveRenderedVersion(renderedVersionRef.current, currentTemplateVersion);

  useEffect(() => {
    if (!isEnabled || hasBeenSubmitted) return;

    if (hasVersionDiverged(renderedVersionRef.current, currentVersion)) {
      setHasTemplateChanged(true);
      setIsDialogOpen(true);
    }
  }, [isEnabled, hasBeenSubmitted, currentTemplateVersion]);

  /**
   * Consulta la versión vigente y, si difiere de la que se dibujó, marca la divergencia.
   * Devuelve `true` si detectó el cambio.
   */
  const detectTemplateChange = async () => {
    const rendered = renderedVersionRef.current;
    if (rendered === null || typeof fetchVersionRef.current !== 'function') return false;

    // Si no se pudo averiguar, `hasVersionDiverged` devuelve false: mejor seguir que molestar por un
    // error de red.
    const latest = toTemplateVersion(await fetchVersionRef.current());
    if (!hasVersionDiverged(rendered, latest)) return false;

    setHasTemplateChanged(true);
    setIsDialogOpen(true);
    return true;
  };

  /*
   * Detección temprana, al volver a la pestaña.
   *
   * La plantilla que se renderiza está congelada a propósito —ver `application-form-page.jsx`—, así
   * que ya no alcanza con mirar lo que hay en memoria: hay que preguntar. Esto es lo que separa
   * "lo que se dibuja", que no cambia bajo los pies del usuario, de "lo que se chequea", que sí
   * está al día. Cubre la carrera típica: la persona deja la pestaña, un admin edita, vuelve.
   */
  useEffect(() => {
    if (!isEnabled || hasBeenSubmitted || hasTemplateChanged) return undefined;

    const handleFocus = () => {
      detectTemplateChange();
    };
    window.addEventListener('focus', handleFocus);

    return () => window.removeEventListener('focus', handleFocus);
  }, [isEnabled, hasBeenSubmitted, hasTemplateChanged]);

  /**
   * Se llama antes de enviar. Devuelve `true` si hay que frenar el envío y mostrar el diálogo.
   *
   * **Vuelve a pedir la versión vigente**, no alcanza con lo que ya se sabe: en edición la plantilla
   * no se re-consulta sola (`refetchOnWindowFocus` está apagado para ese modo), así que un cambio
   * hecho mientras la persona completaba el formulario no se detectaría de ninguna otra forma. Es
   * la mitad "al enviar" de D9.
   */
  const blockSubmitIfTemplateChanged = async () => {
    if (!isEnabled || hasBeenSubmitted) return false;

    if (hasTemplateChanged) {
      setIsDialogOpen(true);
      return true;
    }

    // Si acá falla la consulta, no se frena el envío: que decida el api.
    return detectTemplateChange();
  };

  /** Refresca el formulario, conservando o descartando lo cargado según lo que elija el usuario. */
  const refreshForm = ({ keepDraft, values }) => {
    if (keepDraft) {
      saveFormDraft(draftKey, values);
    } else {
      clearFormDraft(draftKey);
    }

    window.location.reload();
  };

  const closeDialog = () => setIsDialogOpen(false);

  /**
   * Se llama cuando la solicitud se guardó bien. Hace dos cosas: el borrador ya no tiene razón de
   * existir (D6), y a partir de acá **no se chequea más** la versión de la plantilla (D55).
   */
  const markRequestSubmitted = () => {
    setHasBeenSubmitted(true);
    return clearFormDraft(draftKey);
  };

  return {
    hasTemplateChanged,
    isDialogOpen,
    closeDialog,
    refreshForm,
    blockSubmitIfTemplateChanged,
    markRequestSubmitted,
    readDraft: () => readFormDraft(draftKey)
  };
};
